// src/pages/Contact.js

import React, { useEffect, useState } from 'react';
import {
    Box,
    Container,
    Typography,
    CircularProgress,
    Card,
    CardContent,
    IconButton,
    Link,
    Divider,
    Stack
} from '@mui/material';
import {
    Email as EmailIcon,
    Phone as PhoneIcon,
    LocationOn as LocationIcon,
    Language as WebsiteIcon,
    Instagram as InstagramIcon,
    Twitter as TwitterIcon,
    Facebook as FacebookIcon,
} from '@mui/icons-material';
import { supabase } from '../api/supabaseClient';
import { useTranslation } from 'react-i18next';

const ContactPage = () => {
    const { t, i18n } = useTranslation();
    const [aboutData, setAboutData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAbout = async () => {
            setLoading(true);
            const { data } = await supabase.from('about').select('*').single();
            setAboutData(data || {});
            setLoading(false);
        };
        fetchAbout();
    }, []);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!aboutData) {
        return (
            <Container sx={{ mt: 4 }}>
                <Typography color="error" align="center">
                    {t('Contact information could not be loaded')}
                </Typography>
            </Container>
        );
    }

    const {
        email,
        phone,
        address,
        website,
        instagram,
        twitter,
        facebook,
    } = aboutData;

    return (
        <Box
            sx={{
                    height: 'calc(100vh - 64px)',   // sabit yükseklik
                    boxSizing: 'border-box',        // padding’i yükseklik hesabına dahil et
                width: '100%',
                bgcolor: 'background.default',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 8,
                    overflowY: 'hidden',            // Box artık taşmasa da yinede kapatmak istersen
            }}
        >
            <Container maxWidth="sm">
                <Card
                    sx={{
                        borderRadius: 4,
                        boxShadow: 8,
                        bgcolor: '#232323',   // Sadece Card arka planı açık siyah
                        color: '#fff',
                        p: 3,
                    }}
                >
                    <CardContent>
                        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>
                            {t('contact')}
                        </Typography>
                        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.15)', mb: 3 }} />

                        <Stack spacing={2}>
                            {email && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <EmailIcon fontSize="small" />
                                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                        <Link
                                            href={`mailto:${email}`}
                                            underline="hover"
                                            sx={{ color: 'inherit' }}
                                        >
                                            {email}
                                        </Link>
                                    </Typography>
                                </Box>
                            )}
                            {phone && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <PhoneIcon fontSize="small" />
                                    <Typography variant="body1">{phone}</Typography>
                                </Box>
                            )}
                            {address && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <LocationIcon fontSize="small" />
                                    <Typography variant="body1">{address}</Typography>
                                </Box>
                            )}
                            {website && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <WebsiteIcon fontSize="small" />
                                    <Typography variant="body1">
                                        <Link
                                            href={website}
                                            target="_blank"
                                            rel="noopener"
                                            underline="hover"
                                            sx={{ color: 'inherit' }}
                                        >
                                            {website.replace(/^https?:\/\//, '')}
                                        </Link>
                                    </Typography>
                                </Box>
                            )}
                        </Stack>

                        {(instagram || twitter || facebook) && (
                            <>
                                <Divider sx={{ bgcolor: 'rgba(255,255,255,0.1)', my: 3 }} />
                                <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                    {instagram && (
                                        <IconButton
                                            component="a"
                                            href={instagram}
                                            target="_blank"
                                            rel="noopener"
                                            sx={{
                                                color: '#fff',
                                                border: '1px solid rgba(255,255,255,0.6)',
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                                            }}
                                        >
                                            <InstagramIcon />
                                        </IconButton>
                                    )}
                                    {twitter && (
                                        <IconButton
                                            component="a"
                                            href={twitter}
                                            target="_blank"
                                            rel="noopener"
                                            sx={{
                                                color: '#fff',
                                                border: '1px solid rgba(255,255,255,0.6)',
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                                            }}
                                        >
                                            <TwitterIcon />
                                        </IconButton>
                                    )}
                                    {facebook && (
                                        <IconButton
                                            component="a"
                                            href={facebook}
                                            target="_blank"
                                            rel="noopener"
                                            sx={{
                                                color: '#fff',
                                                border: '1px solid rgba(255,255,255,0.6)',
                                                '&:hover': { backgroundColor: 'rgba(255,255,255,0.08)' },
                                            }}
                                        >
                                            <FacebookIcon />
                                        </IconButton>
                                    )}
                                </Box>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default ContactPage;
